/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useMemo } from 'react';
import styled from 'styled-components/macro';
import { Layout } from 'components/organisms';
import {
  Text, Spacer, Avatar
} from 'components/atoms';
import { useUserContext, actions } from 'context/UserContext';
import { useRouterContext } from 'hooks';
import { useSelector, useDispatch } from 'react-redux'
import { setEditorUserId } from 'redux/draftsSlice'

import queryString from 'query-string';
import { useSpring, animated } from 'react-spring';
import { computePathEditor } from 'services/generators';
import { addToken, getToken } from 'services/helpers';
import { isProd } from 'services/constants'
import {
  queryUserImage, queryWarmup, logout
} from 'services/requests';
import { CancelToken, isCancel } from 'axios'
import { injectIntl } from 'react-intl'
import { intlType } from 'types'

const Page = ({ intl }) => {
  const [reset, setReset] = useState(false);
  const dispatchRedux = useDispatch()

  const [
    {
      gatherRedirect, userFailure, userAuthenticated, userLoading, user
    },
    dispatch,
  ] = useUserContext();
  const { history } = useRouterContext();
  const {
    draftsAuthorId
  } = useSelector(
    (state) => state.drafts
  );
  const [token, setToken] = useState(getToken());

  const loginPathQueryToken = useMemo(() => {
    const { search, pathname } = new URL(gatherRedirect, 'http://base.url');
    if (pathname !== '/login') return null
    const query = queryString.parse(search)
    return query && query.token
  }, [gatherRedirect])

  const handleSavedDrafts = ({ draftsAuthorId, authenticatedUserId }) => {
    const hasLocalstorage = !!draftsAuthorId
    // set current editor
    if (!hasLocalstorage) {
      dispatchRedux(setEditorUserId(authenticatedUserId))
      return;
    }
    // delete saved drafts when not the same user
    if (draftsAuthorId !== authenticatedUserId) {
      localStorage.removeItem('redux_gather');
    }
  }

  // todo refactor
  useEffect(() => {
    const source = CancelToken.source();

    async function fetchUser(preventLogout) {
      try {
        dispatch({
          type: actions.QUERY_CURRENT_USER_LOADING,
        });
        const { data: currentUser } = await queryWarmup({
          cancelToken: source.token,
          params: {
            initialRequest: true
          }
        });
        const imageData = currentUser.id && isProd ? await queryUserImage(currentUser.id) : null
        const hasImage = imageData && imageData.data && imageData.data.byteLength > 0
        const image = hasImage ? `data:image/jpeg;base64,${Buffer.from(imageData.data, 'binary').toString('base64')}` : null

        dispatch({
          type: actions.QUERY_CURRENT_USER_SUCCESS,
          data: {
            ...currentUser,
            image,
          },
        });
      } catch (error) {
        const cancelled = isCancel(error)

        if (!cancelled) {
          if (!preventLogout) logout()
        }
        dispatch({
          type: actions.QUERY_CURRENT_USER_FAILURE,
        });
      }
    }

    dispatch({
      type: actions.UPDATE_GATHER_PREVIOUS,
      data: true,
    })
    // prevent logout when the user came from the login page with a token query
    const preventLogout = !!loginPathQueryToken
    fetchUser(preventLogout);

    // cleanup function that cancels pending requests
    return () => {
      source.cancel()
    }
  }, [token, loginPathQueryToken]);

  useEffect(() => {
    if (!userLoading && (userFailure || userAuthenticated)) {
      let pathToRedirect = gatherRedirect;

      // when user is not authenticated
      if (userFailure) {
        if (loginPathQueryToken) {
          addToken(loginPathQueryToken);
          setToken(loginPathQueryToken);
        } else {
          setTimeout(() => {
            history.push('/login');
          }, 1000)
        }
      }
      if (userAuthenticated) {
        // deletes local storage if different user
        handleSavedDrafts({ draftsAuthorId, authenticatedUserId: user.id })

        // redirect back to where the user came from
        pathToRedirect = computePathEditor(gatherRedirect);

        // redirect to subweb when user only has one
        if (pathToRedirect === '/' && user && user.subwebs && user.subwebs.length === 1) {
          const [subweb] = user.subwebs
          pathToRedirect = `/${subweb.slug}`
        }

        // timeout because of beautiful animation
        setTimeout(() => {
          history.push(pathToRedirect)
        }, 1000);
      }
    }
  }, [userFailure, userAuthenticated, userLoading, loginPathQueryToken]);

  const loadingAnimation = useSpring({
    from: { opacity: 0.6, transform: 'scale(1)' },
    to: { opacity: 1, transform: 'scale(0.98)' },
    onRest: () => {
      if (userFailure || userAuthenticated) setReset(false);
      setReset(!reset);
    },
    reverse: reset,
    reset,
  });

  const computedMessage = useMemo(() => {
    if (userFailure) return intl.formatMessage({ id: 'loading' })
    if (userAuthenticated) return `Bem-vindo, ${user.name}`;
    return intl.formatMessage({ id: 'loading' });
  }, [userFailure, userAuthenticated, user]);

  // animation between avatars (with image and withotu)
  const [fadeIn, setAnimationStyle] = useSpring(() => ({ opacity: 1 }))
  useEffect(() => {
    setAnimationStyle(
      {
        opacity: userAuthenticated ? 1 : 0,
      }
    )
  }, [userAuthenticated])

  return (
    <Layout modifiers={['fullscreen']}>
      <Center>
        <animated.div style={loadingAnimation}>
          {userAuthenticated
            ? (
              <animated.div style={fadeIn}>
                <Avatar shouldInteract={false} modifiers={['xl9', 'bgLighterS']} name={user.name} image={user.image} />
              </animated.div>
            )
            : <Avatar shouldInteract={false} modifiers={['xl9', 'bgLighterS']} />
          }
        </animated.div>

        <Spacer top="xl2">
          <Text modifiers={['xl2', 'weightBase']}>{computedMessage}</Text>
        </Spacer>
        <animated.div style={fadeIn}>
          <Spacer top="xl">
            <Text modifiers={['content', 'm']}>
            Estamos a carregar o seu Editor
            </Text>
          </Spacer>
        </animated.div>
      </Center>
    </Layout>
  );
};

const Center = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

Page.propTypes = {
  intl: intlType.isRequired,
}

export default injectIntl(Page)
